import { Search, SlidersHorizontal, Plus, X, RotateCcw, Briefcase, ClipboardCheck, Wallet } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { useEffect, useRef, useState } from 'react';

export type Stage = 'pipeline' | 'credit-evaluation' | 'active-portfolio';

interface StageTabsProps {
  activeStage: Stage;
  onStageChange: (stage: Stage) => void;
  counts: {
    pipeline: number;
    creditEvaluation: number;
    activePortfolio: number;
  };
}

const stageConfig = [
  { id: 'pipeline' as Stage, label: 'Pipeline', icon: Briefcase },
  { id: 'credit-evaluation' as Stage, label: 'Credit Evaluation', icon: ClipboardCheck },
  { id: 'active-portfolio' as Stage, label: 'Active Portfolio', icon: Wallet },
];

export function StageTabs({ activeStage, onStageChange, counts }: StageTabsProps) {
  const getCount = (stageId: Stage) => {
    switch (stageId) {
      case 'pipeline':
        return counts.pipeline;
      case 'credit-evaluation':
        return counts.creditEvaluation;
      case 'active-portfolio':
        return counts.activePortfolio;
    }
  };

  return (
    <div className="relative flex w-full max-w-fit items-center rounded-2xl border border-slate-200 bg-slate-50/90 p-1 shadow-[0_8px_30px_rgba(15,23,42,0.05)]">
      {stageConfig.map((stage) => {
        const isActive = activeStage === stage.id;
        const Icon = stage.icon;
        const count = getCount(stage.id);

        return (
          <button
            key={stage.id}
            onClick={() => onStageChange(stage.id)}
            className={cn(
              'group relative flex items-center gap-2 rounded-xl px-3.5 py-2 text-[13px] font-medium transition-all duration-150 focus-ring',
              isActive
                ? 'bg-white text-slate-900 shadow-sm ring-1 ring-[#5da8b4]/15'
                : 'text-slate-500 hover:text-slate-800'
            )}
          >
            <span
              className={cn(
                'flex h-5.5 w-5.5 shrink-0 items-center justify-center rounded-md transition-colors duration-150',
                isActive
                  ? 'bg-[#e7f6f8] text-[#4d9aa6] shadow-sm'
                  : 'bg-white text-slate-400 group-hover:text-slate-700'
              )}
            >
              <Icon className="h-3 w-3" />
            </span>
            <span className="hidden sm:inline">{stage.label}</span>
            <span
              className={cn(
                'flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold',
                isActive ? 'bg-[#5da8b4] text-white' : 'bg-white text-slate-500'
              )}
            >
              {count}
            </span>
            {isActive && (
              <span className="absolute bottom-0 left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-full bg-[#5da8b4]" />
            )}
          </button>
        );
      })}
    </div>
  );
}

export type QuickFilter = string;

export interface QuickFilterOption {
  value: QuickFilter;
  label: string;
}

interface TopBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  quickFilter: QuickFilter;
  onQuickFilterChange: (filter: QuickFilter) => void;
  quickFilterOptions: QuickFilterOption[];
  quickFilterCounts: Record<string, number>;
  activeFilterCount: number;
  onOpenFilters: () => void;
  onAddLead: () => void;
  onClearFilters?: () => void;
}

export function TopBar({
  search,
  onSearchChange,
  quickFilter,
  onQuickFilterChange,
  quickFilterOptions,
  quickFilterCounts,
  activeFilterCount,
  onOpenFilters,
  onAddLead,
  onClearFilters,
}: TopBarProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const hasQuickFilterOptions = quickFilterOptions.length > 0;
  const hasActiveFilters = activeFilterCount > 0 || search !== '';

  return (
    <div className="flex flex-col gap-2.5 xl:flex-row xl:items-center xl:justify-between">
      <div className="flex flex-1 flex-col gap-2.5 lg:flex-row lg:items-center">
        <div
          className={cn(
            'relative w-full transition-all duration-150 lg:max-w-[400px]',
            isSearchFocused && 'lg:max-w-[440px]'
          )}
        >
          <Search
            className={cn(
              'absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 transition-colors duration-150',
              isSearchFocused ? 'text-[#4d9aa6]' : 'text-slate-400'
            )}
          />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search company"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            className={cn(
              'filter-input w-full pl-9 pr-14 focus-ring',
              isSearchFocused && 'shadow-[0_0_0_4px_rgba(93,168,180,0.08)]'
            )}
          />
          {search && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-10 top-1/2 -translate-y-1/2 rounded-full p-1 transition-colors hover:bg-slate-100"
            >
              <X className="h-3 w-3 text-slate-400" />
            </button>
          )}
          <kbd className="absolute right-3 top-1/2 hidden -translate-y-1/2 items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] font-medium text-slate-400 sm:inline-flex">
            <span className="text-xs">Ctrl</span>K
          </kbd>
        </div>

        {hasQuickFilterOptions && (
          <div className="flex w-full flex-wrap items-center overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_4px_18px_rgba(15,23,42,0.04)] lg:w-auto">
            {quickFilterOptions.map((opt, index) => {
              const isActive = quickFilter === opt.value;
              const count = quickFilterCounts[opt.value] ?? 0;

              return (
                <button
                  key={opt.value}
                  onClick={() => onQuickFilterChange(opt.value)}
                  className={cn(
                    'relative flex items-center gap-1.5 whitespace-nowrap px-4 py-2.5 text-[13px] font-medium transition-all duration-150',
                    index > 0 && 'border-l border-slate-200',
                    isActive
                      ? 'bg-[#5da8b4] text-white'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                  )}
                >
                  {isActive && <span className="inline-block h-1.5 w-1.5 rounded-full bg-white" />}
                  {opt.label}
                  <span className={cn('text-[11px] tabular-nums', isActive ? 'text-white/85' : 'text-slate-400')}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="h-9 gap-1.5 rounded-xl px-3 text-[11px] text-slate-500 hover:bg-rose-50 hover:text-rose-600"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}
        <Button
          onClick={onAddLead}
          className="h-9 gap-2 rounded-xl bg-[#3f8b98] px-4 text-[12px] font-semibold text-white shadow-sm hover:bg-[#4d9aa6]"
        >
          <Plus className="h-3 w-3" />
          <span>Add Lead</span>
        </Button>
        <Button
          variant="outline"
          onClick={onOpenFilters}
          className={cn(
            'focus-ring h-9 gap-2 rounded-xl border-slate-200 px-4 text-[12px] font-semibold text-slate-700 transition-all duration-150 hover:bg-slate-50',
            activeFilterCount > 0 && 'border-[#5da8b4]/40 bg-[#3f8b98]/5 text-[#3f7f89]'
          )}
        >
          <SlidersHorizontal
            className={cn('h-3 w-3', activeFilterCount > 0 && 'text-[#3f8b98]')}
          />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="ml-0.5 bg-[#3f8b98] px-1.5 py-0 text-[9px] text-white">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </div>
    </div>
  );
}
